/**
 * Group Service Layer
 *
 * API 호출 및 Domain 모델 변환을 담당
 * Layered Architecture 패턴 적용
 */

import type {
  GroupListApiResponse,
  GroupDetailApiResponse,
  GroupsWithSchoolsAndTanksApiResponse,
  CreateGroupApiRequest,
  AssignSchoolsApiRequest,
  ReleaseSchoolsApiRequest,
  ReleasedGroupsApiResponse,
} from '@/types/api'

import type {
  Group,
  GroupDetail,
  CreateGroupCommand,
  AssignSchoolsCommand,
  ReleaseSchoolsCommand,
} from '@/types/domain'

import {
  toDomainGroup,
  toDomainGroups,
  toDomainGroupDetail,
  toDomainGroupDetails,
  toApiCreateGroup,
  toApiAssignSchools,
  toApiReleaseSchools,
} from '@/types/mappers'

import { getData, postData } from '@/utils'

// ============================================
// Group Queries (조회)
// ============================================

/**
 * Group 목록 조회
 *
 * @returns Domain Group 배열
 */
export async function getGroups(): Promise<Group[]> {
  try {
    const response = await getData<{ success: number; data: GroupListApiResponse }>(
      '/group'
    )

    // Backend가 { success, data } 형태로 감싸서 반환
    const apiResponse = (response as any).data || response

    // API 응답을 Domain 모델로 변환
    return toDomainGroups(apiResponse)
  } catch (error) {
    console.error('Failed to fetch groups:', error)
    throw error
  }
}

/**
 * Group 상세 조회
 *
 * @param groupId - Group ID
 * @returns Domain Group
 */
export async function getGroupById(groupId: string): Promise<Group> {
  try {
    const response = await getData<{ success: number; data: GroupDetailApiResponse }>(
      `/group/${groupId}`
    )

    const apiResponse = (response as any).data || response

    // API 응답을 Domain 모델로 변환
    return toDomainGroup(apiResponse)
  } catch (error) {
    console.error('Failed to fetch group by ID:', error)
    throw error
  }
}

/**
 * Group 상세 조회 (School-Tank 관계 포함)
 *
 * @returns Domain GroupDetail 배열
 */
export async function getGroupsWithDetails(): Promise<GroupDetail[]> {
  try {
    const response = await getData<{
      success: number
      data: GroupsWithSchoolsAndTanksApiResponse
    }>('/group/assigned')

    const apiResponse = (response as any).data || response

    // API 응답을 Domain 모델로 변환
    return toDomainGroupDetails(apiResponse)
  } catch (error) {
    console.error('Failed to fetch groups with details:', error)
    throw error
  }
}

/**
 * Released Group 상세 조회 (School-Tank 관계 포함)
 *
 * @returns Domain GroupDetail 배열 (비관리 그룹)
 */
export async function getReleasedGroups(): Promise<GroupDetail[]> {
  try {
    const response = await getData<{
      success: number
      data: ReleasedGroupsApiResponse
    }>('/group/released')

    const apiResponse = (response as any).data || response

    // API 응답을 Domain 모델로 변환
    // Released groups 응답 구조가 다름: 배열 형태로 School-Tank pair 반환
    const items = Array.isArray(apiResponse) ? apiResponse : []

    // Released groups는 그룹이 없으므로 하나의 가상 그룹으로 묶음
    const groupDetail: GroupDetail = {
      id: 'released',
      name: '비관리',
      items: items.map((item: any) => ({
        school: {
          id: item.school.id,
          name: item.school.name,
          count: item.school.count || null,
          weightPerFish: item.school.weight_per_fish || null,
          startDate: new Date(item.school.start_date),
          endDate: null,
          totalBiomass: item.school_current_information?.total_biomass || null,
          averageWeight: item.school_current_information?.average_weight || null,
          density: null,
          feedingRate: null,
        },
        tank: {
          id: item.tank.id,
          name: item.tank.name,
          aquafarmId: '', // API에 없음
          volume: item.tank.volume,
          radius: item.tank.radius,
          depth: item.tank.depth,
          maxDensity: item.tank.max_density,
        },
      })),
    }

    return [groupDetail]
  } catch (error) {
    console.error('Failed to fetch released groups:', error)
    throw error
  }
}

// ============================================
// Group Commands (생성/수정)
// ============================================

/**
 * Group 생성
 *
 * @param command - CreateGroupCommand
 */
export async function createGroup(command: CreateGroupCommand): Promise<void> {
  try {
    const apiRequest = toApiCreateGroup(command)
    await postData('/group', apiRequest)
  } catch (error) {
    console.error('Failed to create group:', error)
    throw error
  }
}

/**
 * School 할당
 *
 * @param command - AssignSchoolsCommand
 */
export async function assignSchools(command: AssignSchoolsCommand): Promise<void> {
  try {
    const apiRequest = toApiAssignSchools(command)
    await postData(`/group/${command.groupId}/assign-schools`, apiRequest)
  } catch (error) {
    console.error('Failed to assign schools:', error)
    throw error
  }
}

/**
 * School 해제
 *
 * @param command - ReleaseSchoolsCommand
 */
export async function releaseSchools(command: ReleaseSchoolsCommand): Promise<void> {
  try {
    const apiRequest = toApiReleaseSchools(command)
    await postData(`/group/${command.groupId}/release-schools`, apiRequest)
  } catch (error) {
    console.error('Failed to release schools:', error)
    throw error
  }
}

// ============================================
// Legacy Compatibility (기존 API 호환)
// ============================================

/**
 * @deprecated Use getGroups() instead
 *
 * 기존 callback 스타일 API 호환용
 */
export function getGroupList(setState: (groups: Group[]) => void): void {
  getGroups()
    .then((groups) => setState(groups))
    .catch((error) => console.error('getGroupList error:', error))
}

/**
 * @deprecated Use getGroupById() instead
 *
 * 기존 callback 스타일 API 호환용
 */
export function getGroupDetail(groupId: string, setState: (group: Group) => void): void {
  getGroupById(groupId)
    .then((group) => setState(group))
    .catch((error) => console.error('getGroupDetail error:', error))
}

/**
 * @deprecated Use getGroupsWithDetails() instead
 *
 * 기존 callback 스타일 API 호환용
 */
export function getGroupsWithSchoolsAndTanks(setState: (groups: GroupDetail[]) => void): void {
  getGroupsWithDetails()
    .then((groups) => setState(groups))
    .catch((error) => console.error('getGroupsWithSchoolsAndTanks error:', error))
}
